import { INTERNAL_APP_HEADER } from "../api/client";
import { clearSsoSession } from "../hooks/useSsoSession";

const STORAGE_KEY = "coverflow-admin-token";

let inMemoryToken: string | null = null;

// Lets AdminAuthContext (via useSyncExternalStore) re-render whenever any caller
// sets or clears the token — e.g. an invalid-token handler deep in adminApi.
const listeners = new Set<() => void>();
const notify = () => listeners.forEach((fn) => fn());

export function subscribeAdminToken(fn: () => void): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

function getStorage(type: "session" | "local"): Storage | null {
  if (typeof window === "undefined") return null;
  try {
    return type === "session" ? window.sessionStorage : window.localStorage;
  } catch {
    return null;
  }
}

function readFromStorage(): string | null {
  if (inMemoryToken) return inMemoryToken;

  const sessionStore = getStorage("session");
  const localStore = getStorage("local");

  const sessionToken = sessionStore?.getItem(STORAGE_KEY);
  if (sessionToken) {
    inMemoryToken = sessionToken;
    return sessionToken;
  }

  const localToken = localStore?.getItem(STORAGE_KEY);
  if (localToken) {
    inMemoryToken = localToken;
    return localToken;
  }

  return null;
}

export function getAdminToken(): string | null {
  return readFromStorage();
}

export function setAdminToken(token: string, options?: { remember?: boolean }) {
  inMemoryToken = token;
  const sessionStore = getStorage("session");
  const localStore = getStorage("local");

  sessionStore?.setItem(STORAGE_KEY, token);
  if (options?.remember) {
    localStore?.setItem(STORAGE_KEY, token);
  } else {
    localStore?.removeItem(STORAGE_KEY);
  }
  notify();
}

// Ends the whole client session: also drops the SSO auth state so every
// "invalid admin_token" handler (adminApi, loginAsUser, streamAgent) sends the
// user back to /login instead of leaving them on a page where every call fails.
export function clearAdminToken() {
  clearSsoSession();
  inMemoryToken = null;
  getStorage("session")?.removeItem(STORAGE_KEY);
  getStorage("local")?.removeItem(STORAGE_KEY);
  notify();
}

// Raw fetches (login-as, agent stream, usage) bypass api/client's get(). Without
// the app header, require_super_admin reads the customer's auth-token instead of
// the SSO admin's admin-auth-token. Callers must also send credentials: "include".
export function adminTokenHeader(): Record<string, string> {
  const token = getAdminToken();
  return token ? { ...INTERNAL_APP_HEADER, "admin-token": token } : { ...INTERNAL_APP_HEADER };
}

export function isRemembered(): boolean {
  const localStore = getStorage("local");
  return Boolean(localStore?.getItem(STORAGE_KEY));
}

// The one error shape the backend uses for "this admin token is invalid/expired".
export function isInvalidAdminTokenError(status: number, body: unknown): boolean {
  return status === 403 && (body as { error?: unknown } | null | undefined)?.error === "invalid admin_token";
}

